import React, { useEffect } from 'react';

const PrivacyPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | InTimelog';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white pt-44 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-navy-900 mb-2">InTimeLog Privacy Policy</h1>
        <p className="text-sm text-navy-600">Effective Date: May 30, 2026</p>
        <p className="text-sm text-navy-600 mb-8">Last Updated: May 30, 2026</p>

        <div className="prose prose-navy max-w-none space-y-6 text-navy-700">
          <p>
            At InTimelog, we respect your privacy and are committed to protecting your information.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, disclose, and protect your information when you interact with our:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Website</li>
            <li>Mobile applications</li>
            <li>Dispatch systems</li>
            <li>Communication channels</li>
            <li>Contract account services</li>
          </ul>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">1. Information We Collect</h2>
            <p>We may collect:</p>

            <h3 className="text-lg font-semibold text-navy-900 mb-2 mt-4">Personal Information</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Pickup and dropoff addresses</li>
              <li>Flight details</li>
              <li>Employer or company information</li>
            </ul>

            <h3 className="text-lg font-semibold text-navy-900 mb-2 mt-4">Device & Technical Information</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>IP address</li>
              <li>Browser type</li>
              <li>Device identifiers</li>
              <li>GPS location</li>
              <li>Operating system</li>
              <li>App usage information</li>
              <li>Log data</li>
            </ul>

            <h3 className="text-lg font-semibold text-navy-900 mb-2 mt-4">Communication Information</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>SMS interactions</li>
              <li>Emails</li>
              <li>Customer support communications</li>
              <li>Recorded phone calls where permitted by law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">2. Location Information</h2>
            <p>Our applications and dispatch systems may collect precise or approximate location information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Coordinate rides</li>
              <li>Improve dispatching</li>
              <li>Monitor trips</li>
              <li>Enhance safety</li>
              <li>Provide operational updates</li>
            </ul>
            <p>You may disable location permissions through your device settings, though some services may not function properly.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">3. How We Use Information</h2>
            <p>We may use collected information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide transportation services</li>
              <li>Coordinate transportation services</li>
              <li>Coordinate drivers</li>
              <li>Improve operations</li>
              <li>Send notifications</li>
              <li>Prevent fraud</li>
              <li>Monitor safety</li>
              <li>Process contract invoicing</li>
              <li>Respond to customer service inquiries</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">4. SMS & Mobile Privacy</h2>
            <p>By opting into SMS communications, you agree to receive messages related to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Ride updates</li>
              <li>Driver notifications</li>
              <li>Service dispatch notifications</li>
              <li>Operational alerts</li>
            </ul>
            <p>Message frequency varies.</p>
            <p>Message and data rates may apply.</p>
            <p>Reply STOP to unsubscribe.</p>
            <p>No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. SMS opt-in information and consent will not be shared with third parties.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">5. Sharing Information</h2>
            <p>We may share information with:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Drivers and transportation partners</li>
              <li>Technology vendors</li>
              <li>Cloud hosting providers</li>
              <li>Customer support providers</li>
              <li>Contract account holders (hotels, corporate clients, airlines)</li>
              <li>Government authorities when legally required</li>
            </ul>

            <p className="mt-4">We do not sell personal information.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">6. Data Retention</h2>
            <p>We retain information:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>As long as necessary to provide services</li>
              <li>To comply with legal obligations</li>
              <li>To resolve disputes</li>
              <li>To enforce agreements</li>
              <li>For fraud prevention and security purposes</li>
            </ul>
            <p>Service records are retained permanently for operational, tax, and legal purposes.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">7. Third-Party Services</h2>
            <p>Our website or applications may contain links or integrations with third-party services.</p>
            <p>We are not responsible for third-party privacy practices, policies, or content.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">8. Data Security</h2>
            <p>We implement commercially reasonable administrative, technical, and physical safeguards designed to protect your information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">9. Children's Privacy & Minor Restrictions</h2>
            <p>InTimeLog's services are intended only for individuals who are legally permitted to arrange and use transportation services under applicable law.</p>
            <p>We do not knowingly collect personal information from children under the age of 13.</p>
            <p>InTimeLog does not provide transportation services for unaccompanied minors. Passengers under the age of 18 must be accompanied by a parent, legal guardian, or authorized adult.</p>
            <p>If we become aware that personal information has been collected from a child in violation of this policy, we may delete such information and suspend or terminate associated accounts or service requests.</p>
            <p>Parents or guardians who believe a child has provided us with personal information may contact us to request deletion.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">10. Sensitive Information & Medical Transportation Disclaimer</h2>
            <p>InTimeLog is not a healthcare provider, medical transportation provider, ambulance service, or ADA paratransit provider.</p>
            <p>Customers should not provide sensitive medical information unless specifically required for operational or legal purposes.</p>
            <p>We are not responsible for the handling of medical, disability, or health-related information voluntarily disclosed by passengers beyond what is required to provide requested services.</p>
            <p>Special accommodation requests are subject to operational availability and may not always be supported.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">11. Location & Ride Monitoring</h2>
            <p>To improve safety, operational efficiency, fraud prevention, and dispatch coordination, InTimeLog may collect and process:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>GPS location data</li>
              <li>Trip route information</li>
              <li>Driver telematics</li>
              <li>Device information</li>
              <li>Ride timestamps</li>
              <li>Communication records</li>
            </ul>
            <p>This information may be used to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Coordinate pickups and drop-offs</li>
              <li>Investigate complaints</li>
              <li>Monitor service quality</li>
              <li>Improve operational performance</li>
              <li>Enforce safety policies</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>By using our services, you consent to such collection and processing.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">12. Communication Recording & Monitoring</h2>
            <p>Calls, messages, chats, emails, and other communications with InTimeLog may be monitored or recorded for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Quality assurance</li>
              <li>Training</li>
              <li>Safety investigations</li>
              <li>Fraud prevention</li>
              <li>Dispute resolution</li>
              <li>Legal compliance</li>
            </ul>
            <p>By communicating with us, you consent to such monitoring where permitted by applicable law.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">13. Data Disclaimer</h2>
            <p>While we implement reasonable administrative, technical, and physical safeguards designed to protect personal information, no method of transmission or storage is completely secure.</p>
            <p>Accordingly, InTimeLog cannot guarantee absolute security of any information transmitted to or stored by our systems.</p>
            <p>Use of our services is at your own risk.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">14. Your Rights</h2>
            <p>Depending on applicable law, you may have rights to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access your information</li>
              <li>Correct inaccuracies</li>
              <li>Request deletion</li>
              <li>Opt out of certain communications</li>
              <li>Request data portability</li>
            </ul>
            <p>Requests may be subject to identity verification and legal limitations.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">15. Texas & U.S. Compliance</h2>
            <p>We strive to comply with applicable:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Texas transportation regulations</li>
              <li>Federal transportation requirements</li>
              <li>Privacy laws</li>
              <li>TCPA communications requirements</li>
            </ul>
            <p className="mt-4">Dallas transportation complaints may be directed to:</p>
            <p className="ml-4">
              Department of Aviation – Transportation Regulation Division<br />
              3448 West Mockingbird Lane<br />
              Dallas, Texas 75235<br />
              Phone: (214) 670-3161
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">16. Changes to This Policy</h2>
            <p>We may update this Privacy Policy periodically.</p>
            <p>Updated versions become effective upon posting.</p>
            <p>Continued use of services constitutes acceptance of the revised policy.</p>
          </section>

          <div className="mt-12 pt-6 border-t border-navy-200">
            <h3 className="text-lg font-semibold text-navy-900 mb-3">Contact Information</h3>
            <p>
              For questions regarding this Privacy Policy, contact:<br />
              <strong>InTimeLog</strong><br />
              Dallas, Texas, USA<br />
              Email: <a href="mailto:support@intimelog.com" className="text-primary-600 hover:underline">support@intimelog.com</a><br />
              Phone: <a href="tel:+14695632674" className="text-primary-600 hover:underline">(469) 563-2674</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
